import { AllowableAssortmentService } from '@/services/allowable-assortment.service';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import { AllowableProductModelDto, CaseType, OrderType, ProductMainGroups } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormat } from './format';
import { useNotification } from './useNotification';

interface MainGroupsProductModel {
  mainGroup: ProductMainGroups;
  productModels: AllowableProductModelDto[];
}

export function useAllowableAssortment() {
  const userStore = useUserStore();
  const { formatDateToIsoStringIgnoringTime } = useFormat();
  const notify = useNotification();
  const { t } = useI18n();
  const productMatrixStore = useProductMatrixStore();

  const { selectedAccount: account } = storeToRefs(userStore);

  const loading = ref(false);
  const searchText = ref('');
  const assortments = ref<AllowableProductModelDto[]>([]);
  const allowableProductModels = ref<Map<string, AllowableProductModelDto>>(
    new Map<string, AllowableProductModelDto>(),
  );
  const selectedMainGroup = ref(ProductMainGroups.shoes);

  const allowableAssortmentService = new AllowableAssortmentService();

  const mainGroupsProductModels = computed(() => {
    const mainGroupsProductModelMap = initializeMainGroupsProductModels();

    for (const allowableProductModel of visibleProductModels.value) {
      const productGroup = mainGroupsProductModelMap.get(allowableProductModel.mainGroup);

      if (!productGroup) {
        continue;
      }

      productGroup.productModels.push(allowableProductModel);
      mainGroupsProductModelMap.set(allowableProductModel.mainGroup, productGroup);
    }

    const models = Array.from(mainGroupsProductModelMap.values());

    return models.filter((model) => model.productModels.length);
  });

  const visibleProductModels = computed(() => {
    const filteredProductModels: AllowableProductModelDto[] = [];

    if (searchText.value.trim() !== '') {
      const searchTextLowerCase = searchText.value.toLocaleLowerCase();

      for (const allowableAssortment of assortments.value) {
        if (
          allowableAssortment.name.toLocaleLowerCase().includes(searchTextLowerCase) ||
          allowableAssortment.itemNumbers?.some((itemNumber: string) =>
            itemNumber.toLocaleLowerCase().startsWith(searchTextLowerCase),
          )
        ) {
          filteredProductModels.push(allowableAssortment);
        }
      }
    } else {
      return assortments.value;
    }

    return filteredProductModels;
  });

  function initializeMainGroupsProductModels() {
    const mainGroups = [ProductMainGroups.shoes, ProductMainGroups.apparel, ProductMainGroups.accessories];

    return new Map<ProductMainGroups, MainGroupsProductModel>(
      mainGroups.map((mainGroup) => [
        mainGroup,
        {
          mainGroup,
          productModels: [],
        },
      ]),
    );
  }

  function setSelectedMainGroup(mainGroup: ProductMainGroups) {
    selectedMainGroup.value = mainGroup;
  }

  async function fetchAllowableAssortments(
    orderType: OrderType | CaseType,
    requestedDate: string,
    orderNumber?: string,
  ) {
    loading.value = true;

    const allowableAssortments = await allowableAssortmentService.getAllowableAssortment(
      account.value?.accountNumber,
      orderType,
      formatDateToIsoStringIgnoringTime(requestedDate),
      orderNumber,
    );

    if (allowableAssortments.length === 0) {
      loading.value = false;

      notify.notifyWarning(
        t(orderType === OrderType.PreOrder ? 'matrix.no_assortment_pre_order' : 'matrix.no_assortment_re_order'),
      );

      return;
    }

    const sortedAllowableAssortments = sortAllowableAssortments(allowableAssortments);
    assortments.value = sortedAllowableAssortments;
    setProductModels(sortedAllowableAssortments);

    loading.value = false;
  }

  function sortAllowableAssortments(allowableAssortments: AllowableProductModelDto[] = []) {
    return allowableAssortments.sort((productA, productB) => productA.name.localeCompare(productB.name));
  }

  const setProductModels = (allowableAssortments: AllowableProductModelDto[] = []) => {
    const assortmentsMap = new Map<string, AllowableProductModelDto>();

    allowableAssortments.forEach((assortment) => {
      assortmentsMap.set(assortment.name, assortment);
    });

    productMatrixStore.setProductModels(assortmentsMap);
  };

  return {
    allowableProductModels,
    loading,
    searchText,
    fetchAllowableAssortments,
    visibleProductModels,
    setSelectedMainGroup,
    selectedMainGroup,
    mainGroupsProductModels,
  };
}
